import {useFormatCurrency} from '@aboutyou/i18n';
import {styled} from '@linaria/react';
import React from 'react';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {Price, PriceV2, Reductions} from '../../../types/Price';
import {Text} from '../../atoms/Text/Text';
import {FinalPrice} from '../FinalPrice/FinalPrice';

export interface PriceBoxHorizontalProps {
  secondaryPriceLabel?: string;
  className?: string;
  large?: boolean;
  reductions?: Reductions;
  price: Price | PriceV2;
}

export function PriceBoxHorizontal(props: PriceBoxHorizontalProps) {
  const formatPrice = useFormatCurrency();
  const {secondaryPriceLabel, className, large, reductions, price} = props;

  const isBackgroundColorAvailable = Boolean(
    'finalPrice' in price
      ? price.finalPrice.priceBackgroundColor
      : price.priceBackgroundColor,
  );

  if (reductions && reductions.length && !('finalPrice' in price)) {
    return (
      <Wrapper
        className={className}
        data-testid="priceBox"
        data-large={large}
        $hasSecondaryPriceLabel={Boolean(secondaryPriceLabel)}
        addExtraMargin={isBackgroundColorAvailable}
      >
        <StyledFinalPrice price={price} data-testid="finalPrice" />
        <ReductionWrapper>
          {reductions.map((reduction, index) => {
            const formattedPrice = formatPrice(
              reduction.strikePrice.amount,
              reduction.strikePrice.currencyCode,
            );

            return (
              <SubLabel
                key={reduction.strikePrice.amount + index}
                striked
                data-testid={`StruckPrice${index}`}
                weight="mediumBold"
              >
                {formattedPrice}
              </SubLabel>
            );
          })}
        </ReductionWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      className={className}
      data-testid="priceBox"
      data-large={large}
      $hasSecondaryPriceLabel={Boolean(secondaryPriceLabel)}
      addExtraMargin={isBackgroundColorAvailable}
    >
      <StyledFinalPrice price={price} data-testid="finalPrice" />
    </Wrapper>
  );
}

const labelStyles = `
  direction: initial;
  flex-shrink: 0;
  font-size: inherit;
  line-height: ${FONT_SIZES.px18};

  [data-no-reductions] &:not(:last-child) {
    margin-right: 4px;
  }
`;

const ReductionWrapper = styled.div`
  display: flex;
  gap: 4px;
`;

const StyledFinalPrice = styled(FinalPrice)`
  &&& {
    font-size: ${FONT_SIZES.px12};
    font-weight: ${FONT_WEIGHTS.mediumBold};
    margin: 0;
  }
`;

const PriceLabel = styled(Text)<{
  $inline?: boolean;
}>`
  display: ${({$inline = true}) => ($inline ? 'inline-block' : 'block')};
  ${labelStyles};
`;

const SecondaryPriceLabel = styled.span`
  color: ${COLORS.Grey1};
  font-size: ${FONT_SIZES.px10};
  font-weight: ${FONT_WEIGHTS.bold};
`;

const SubLabel = styled(Text)<{
  $large?: boolean;
  $inline?: boolean;
}>`
  display: ${({$inline = true}) => ($inline ? 'inline-block' : 'block')};
  color: ${COLORS.strikePriceGrey};
  ${labelStyles};
`;

const Wrapper = styled.div<{
  $hasSecondaryPriceLabel: boolean;
  addExtraMargin: boolean;
}>`
  display: flex;
  flex-direction: ${({$hasSecondaryPriceLabel}) =>
    $hasSecondaryPriceLabel ? 'column' : 'row'};
  align-items: ${({$hasSecondaryPriceLabel}) =>
    $hasSecondaryPriceLabel ? 'undefined' : 'flex-end'};
  font-size: ${FONT_SIZES.px12};
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: ${props => (props.addExtraMargin ? '0.5em' : '0 0.5em')};
  margin: ${props => (props.addExtraMargin ? '4px 0 2px 0' : 'initial')};

  div[data-original-price='true'] span {
    font-size: ${FONT_SIZES.px9};
    font-weight: ${FONT_WEIGHTS.mediumBold};
    line-height: 150%;
    color: ${COLORS['Text.Grey']};
  }

  &[data-large='true'] {
    ${PriceLabel} {
      font-size: ${FONT_SIZES.px18};
      font-weight: ${FONT_WEIGHTS.bold};
    }
    ${SecondaryPriceLabel} {
      font-size: ${FONT_SIZES.px14};
      font-weight: ${FONT_WEIGHTS.bold};
    }
    ${SubLabel} {
      font-size: ${FONT_SIZES.px12};
      font-weight: ${FONT_WEIGHTS.mediumBold};
    }
  }
`;
